/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * GLTF fillStyle 타겟 노드 선택 에디터.
 * Specific 탭에서 GLTF 모델의 Mesh 노드 목록을 체크박스로 표시하고,
 * 선택된 노드 이름을 fillStyleTargets 배열로 저장한다.
 * 마우스 오버 시 해당 노드를 OutlinePass로 강조한다.
 */

import '@operato/i18n/ox-i18n.js'

import { css, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'

@customElement('property-editor-gltf-fill-targets')
export default class GLTFFillTargetsEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    css`
      .node-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
        max-height: 200px;
        overflow-y: auto;
        font-size: 12px;
      }

      .node-item {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 4px;
        cursor: pointer;
        border-radius: 2px;
      }

      .node-item:hover {
        background: rgba(242, 101, 34, 0.15);
      }

      .toolbar {
        display: flex;
        gap: 2px;
        margin-bottom: 2px;
      }

      .toolbar button {
        font-size: 11px;
        padding: 1px 6px;
        border: 1px solid var(--md-sys-color-outline, #ccc);
        border-radius: 2px;
        background: transparent;
        color: var(--md-sys-color-on-surface, #333);
        cursor: pointer;
      }

      .toolbar button:hover {
        background: rgba(0, 0, 0, 0.06);
      }

      .empty {
        color: var(--md-sys-color-outline, #999);
        font-size: 11px;
        padding: 4px;
      }
    `
  ]

  @property({ type: String }) declare src: string | undefined

  @state() declare private _meshNames: string[]

  private _component: any = null

  updated(changes: PropertyValues<this>) {
    if (changes.has('src')) {
      this.dispatchEvent(
        new CustomEvent('i-need-selected', {
          bubbles: true,
          composed: true,
          detail: {
            callback: (selected: any[]) => {
              this._component = selected?.[0]
              this._refreshMeshNames()
            }
          }
        })
      )
    }
  }

  private _refreshMeshNames() {
    if (!this._component) {
      this._meshNames = []
      return
    }

    const ro = this._component.realObject as any
    if (!ro?.getNode) {
      this._meshNames = []
      return
    }

    // Mesh 노드만 필터링 — 그룹/본 등은 fillStyle 적용 대상이 아님
    this._meshNames = ((this._component.nodeNames as string[]) ?? []).filter((name: string) => {
      const node = ro.getNode(name)
      return node?.isMesh
    })
  }

  editorTemplate(value: any, _spec: PropertySpec): TemplateResult {
    const targets: string[] = Array.isArray(value) ? value : []

    // GLTF 로드 완료 후 nodeNames가 채워지므로, 비어있으면 재시도
    if (!this._meshNames?.length) {
      this._refreshMeshNames()
    }

    return html`
      <fieldset fullwidth>
        <legend><ox-i18n msgid="label.fill-targets">fillStyle Targets</ox-i18n></legend>
        ${(this._meshNames?.length ?? 0) > 0
            ? html`<div class="toolbar">
                <button type="button" @click=${() => this._setAll(true)}>All</button>
                <button type="button" @click=${() => this._setAll(false)}>None</button>
              </div>`
            : ''}
        <div class="node-list">
          ${(this._meshNames?.length ?? 0) === 0
            ? html`<div class="empty">
                <ox-i18n msgid="label.no-gltf-meshes">No meshes available</ox-i18n>
              </div>`
            : (this._meshNames ?? []).map(
                name => html`
                  <label
                    class="node-item"
                    @mouseenter=${() => this._highlightNode(name)}
                    @mouseleave=${() => this._clearHighlight()}
                  >
                    <input
                      type="checkbox"
                      .checked=${targets.includes(name)}
                      @change=${(e: Event) => {
                        e.stopPropagation()
                        this._onToggle(name, (e.target as HTMLInputElement).checked)
                      }}
                    />
                    <span>${name}</span>
                  </label>
                `
              )}
        </div>
      </fieldset>
    `
  }

  private _setAll(select: boolean) {
    const newValue = select ? [...(this._meshNames ?? [])] : undefined

    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            selected[0].set('fillStyleTargets', newValue)
          }
        }
      })
    )
  }

  private _onToggle(name: string, checked: boolean) {
    const current = [...(Array.isArray(this.value) ? this.value : [])]
    if (checked) {
      if (!current.includes(name)) current.push(name)
    } else {
      const idx = current.indexOf(name)
      if (idx >= 0) current.splice(idx, 1)
    }

    const newValue = current.length > 0 ? current : undefined

    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            selected[0].set('fillStyleTargets', newValue)
          }
        }
      })
    )
  }

  // --- Outline 강조 ---

  private _highlightNode(name: string) {
    const ro = this._component?.realObject as any
    if (!ro?.getNode) return

    const node = ro.getNode(name)
    if (!node) return

    this._getRendererManager()?.setOutlineObjects([node])
    this._component?.invalidate()
  }

  private _clearHighlight() {
    const ro = this._component?.realObject as any
    if (!ro) return

    // 선택 상태의 아웃라인 복원 (전체 GLTF 오브젝트)
    this._getRendererManager()?.setOutlineObjects(ro.object3d ? [ro.object3d] : [])
    this._component?.invalidate()
  }

  private _getRendererManager(): any {
    const ro = this._component?.realObject as any
    const container = ro?.threeContainer as any
    // ThreeContainer._capability 또는 ModelLayer._threeCapability
    return container?._capability?.rendererManager ?? container?._threeCapability?.rendererManager ?? null
  }
}
